import React, { useEffect, useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  Search, 
  Car, 
  Sparkles, 
  CheckCircle2, 
  Info, 
  ChevronLeft, 
  ChevronRight, 
  AlertCircle,
  Building2,
  Calendar,
  Users
} from 'lucide-react';
import { congresoPublicoApi } from '../services/congresoPublicoApi';
import { PadronSorteoPaginado, CongresoPublico } from '../types/congreso.types';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';

export const PadronSorteoView: React.FC = () => {
  const { idCongreso: paramIdCongreso } = useParams<{ idCongreso?: string }>();
  const [congreso, setCongreso] = useState<CongresoPublico | null>(null);
  const [loadingCongreso, setLoadingCongreso] = useState(true);

  const [padron, setPadron] = useState<PadronSorteoPaginado | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 12;

  // 1. Cargar datos del congreso activo o por id
  useEffect(() => {
    let isMounted = true;
    const cargarCongreso = async () => {
      try {
        setLoadingCongreso(true);
        let c: CongresoPublico | null = null;
        if (paramIdCongreso && !isNaN(Number(paramIdCongreso))) {
          c = await congresoPublicoApi.getCongresoPorId(Number(paramIdCongreso));
        } else {
          c = await congresoPublicoApi.getCongresoActivo();
        }
        if (isMounted) {
          setCongreso(c);
        }
      } catch (err) {
        console.error('Error cargando congreso:', err);
      } finally {
        if (isMounted) setLoadingCongreso(false);
      }
    };

    cargarCongreso();
    return () => { isMounted = false; };
  }, [paramIdCongreso]);

  // 2. Debounce para el input de búsqueda (350ms)
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1); // Reset a la primera página al cambiar búsqueda
    }, 350);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  // 3. Cargar el padrón paginado
  useEffect(() => {
    if (!congreso?.idCongreso) return;

    let isMounted = true;
    const fetchPadron = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await congresoPublicoApi.getPadronSorteo(
          congreso.idCongreso,
          debouncedSearch,
          undefined,
          page,
          pageSize
        );
        if (isMounted) {
          setPadron(data);
        }
      } catch (err: any) {
        if (isMounted) {
          console.error('Error al consultar padrón de sorteo:', err);
          setError('No fue posible cargar el padrón del sorteo en este momento. Por favor, reintente más tarde.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchPadron();
    return () => { isMounted = false; };
  }, [congreso?.idCongreso, debouncedSearch, page]);

  const volverUrl = useMemo(() => {
    if (paramIdCongreso) return `/evento/${paramIdCongreso}`;
    return '/';
  }, [paramIdCongreso]);

  const fechaCorteFormateada = useMemo(() => {
    const rawDate = padron?.fechaCongelamientoPadron || padron?.fechaLimitePadron;
    if (!rawDate) return null;
    try {
      const d = new Date(rawDate);
      return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return null;
    }
  }, [padron?.fechaCongelamientoPadron, padron?.fechaLimitePadron]);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col relative overflow-x-hidden">
      {/* Background Decorativo idéntico a la landing */}
      <div className="absolute inset-0 opacity-15 pointer-events-none bg-[radial-gradient(#00A3E0_1px,transparent_1px)] [background-size:24px_24px]"></div>

      <Navbar />

      <main className="flex-1 pt-28 pb-20 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Barra superior de navegación / Volver */}
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
            <Link
              to={volverUrl}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-slate-200 text-brand-navy hover:text-brand-cyan hover:border-brand-cyan/40 text-sm font-semibold shadow-sm transition-all duration-200 group"
            >
              <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
              <span>Volver al Congreso</span>
            </Link>

            <div className="flex flex-wrap items-center gap-2.5">
              {padron && padron.totalCount > 0 && !debouncedSearch && (
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold shadow-xs">
                  <Users className="w-3.5 h-3.5 text-emerald-600" />
                  <span>{padron.totalCount.toLocaleString('es-AR')} afiliados habilitados</span>
                </div>
              )}

              {fechaCorteFormateada && (
                <div className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 border border-slate-200 text-slate-600 text-xs font-medium">
                  <Calendar className="w-3.5 h-3.5 text-slate-500" />
                  <span>Padrón oficial al {fechaCorteFormateada}</span>
                </div>
              )}
            </div>
          </div>

          {/* Banner Hero del Padrón */}
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#001D36] via-brand-navy to-[#082B4D] border border-emerald-500/30 p-6 sm:p-10 shadow-2xl text-white mb-10">
            {/* Glows ambientales sutiles */}
            <div className="absolute top-0 right-0 -mt-10 -mr-10 w-72 h-72 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 left-0 -mb-10 -ml-10 w-60 h-60 bg-brand-cyan/10 rounded-full blur-3xl pointer-events-none" />

            <div className="relative z-10 max-w-3xl space-y-3 text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-emerald-300 text-xs font-bold uppercase tracking-wider">
                <Car className="w-3.5 h-3.5 text-emerald-400" />
                <span>Consulta Oficial de Habilitados</span>
              </div>

              <h1 className="text-2xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight">
                {padron?.nombreSorteo || 'Sorteo Oficial de Auto 0KM'}
              </h1>

              <p className="text-slate-200 text-sm sm:text-base leading-relaxed">
                Consultá tu inclusión en el padrón de matriculados habilitados para el sorteo del vehículo 0KM y conocé tus chances acumuladas según tus aportes.
              </p>

              {/* Explicación de Chances */}
              <div className="pt-2 flex flex-wrap gap-2 text-xs text-slate-300">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-emerald-950/60 border border-emerald-500/30 text-emerald-200">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                  <strong>1 chance:</strong> Aportes al día
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-emerald-950/60 border border-emerald-500/30 text-emerald-200">
                  <Sparkles className="w-3.5 h-3.5 text-emerald-400" />
                  <strong>+1 chance:</strong> 1.º Semestre ABAO
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-emerald-950/60 border border-emerald-500/30 text-emerald-200">
                  <Sparkles className="w-3.5 h-3.5 text-emerald-400" />
                  <strong>+1 chance:</strong> 2.º Semestre ABAO
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-emerald-500/20 border border-emerald-400/40 text-white font-semibold">
                  Hasta 3 chances máx.
                </span>
              </div>
            </div>
          </div>

          {/* Caja de Búsqueda */}
          <div className="bg-white rounded-2xl border border-slate-200/90 p-4 sm:p-6 shadow-sm mb-8">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="relative flex-1 w-full">
                <Search className="w-5 h-5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscá por DNI o Apellido y Nombre..."
                  className="w-full pl-12 pr-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-cyan/40 focus:border-brand-cyan text-sm sm:text-base text-slate-900 placeholder-slate-400 bg-slate-50/50"
                  aria-label="Buscar en padrón"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-400 hover:text-slate-600 px-2 py-1 rounded"
                    aria-label="Limpiar búsqueda"
                  >
                    Limpiar
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Contenido de Resultados / Estados */}
          {loadingCongreso || loading ? (
            <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-slate-200 shadow-sm">
              <div className="w-12 h-12 border-4 border-brand-cyan/20 border-t-brand-cyan rounded-full animate-spin mb-4"></div>
              <p className="text-sm font-semibold text-brand-navy">Consultando padrón oficial...</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-8 text-center max-w-xl mx-auto">
              <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
              <h3 className="text-base font-bold text-red-900 mb-1">Ocurrió un inconveniente</h3>
              <p className="text-sm text-red-700 mb-4">{error}</p>
              <button
                type="button"
                onClick={() => setPage(1)}
                className="px-4 py-2 rounded-xl bg-red-600 text-white font-semibold text-xs sm:text-sm hover:bg-red-700 transition"
              >
                Reintentar
              </button>
            </div>
          ) : !padron || padron.items.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center max-w-lg mx-auto shadow-sm">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4 text-slate-400">
                <Search className="w-8 h-8" />
              </div>
              <h3 className="text-lg font-bold text-slate-800 mb-1">
                {searchTerm ? 'No se encontraron resultados' : 'Padrón no disponible'}
              </h3>
              <p className="text-sm text-slate-500 leading-relaxed mb-4">
                {searchTerm 
                  ? `No se encontró ningún matriculado habilitado que coincida con "${searchTerm}". Verificá si el DNI o apellido están bien escritos.`
                  : 'El padrón de matriculados habilitados se publicará una vez procesado el corte correspondiente.'}
              </p>
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-xs transition"
                >
                  Restablecer búsqueda
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              {/* Barra informativa de conteo por afiliado */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-1 text-sm text-slate-600">
                <div className="flex items-center gap-2 font-medium">
                  <Users className="w-4 h-4 text-emerald-600 shrink-0" />
                  {debouncedSearch ? (
                    <span>
                      Mostrando <strong className="text-slate-900 font-bold">{padron.totalCount.toLocaleString('es-AR')}</strong> {padron.totalCount === 1 ? 'afiliado encontrado' : 'afiliados encontrados'} para &ldquo;{debouncedSearch}&rdquo;
                    </span>
                  ) : (
                    <span>
                      Total de afiliados habilitados: <strong className="text-slate-900 font-bold">{padron.totalCount.toLocaleString('es-AR')}</strong>
                    </span>
                  )}
                </div>
              </div>

              {/* Grilla Responsive de Matriculados */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {padron.items.map((item, idx) => (
                  <div
                    key={`${item.dni}-${idx}`}
                    className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-sm hover:shadow-md hover:border-emerald-500/40 transition-all duration-200 flex flex-col justify-between text-left"
                  >
                    <div>
                      {/* Cabecera con Nombre y Badge de Chances */}
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <h3 className="text-base font-bold text-brand-navy leading-snug">
                          {item.nombreCompleto}
                        </h3>
                        <div className="shrink-0">
                          {item.totalChances === 3 ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-black bg-amber-50 border border-amber-300 text-amber-800 shadow-xs">
                              <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                              3 CHANCES
                            </span>
                          ) : item.totalChances === 2 ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold bg-emerald-50 border border-emerald-300 text-emerald-800">
                              <Sparkles className="w-3.5 h-3.5 text-emerald-600" />
                              2 CHANCES
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold bg-slate-100 border border-slate-200 text-slate-700">
                              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                              1 CHANCE
                            </span>
                          )}
                        </div>
                      </div>

                      {/* DNI y Colegio */}
                      <div className="space-y-1 text-xs text-slate-500 mb-3">
                        <div>
                          <span className="font-semibold text-slate-700">DNI:</span> {item.dni}
                        </div>
                        <div className="flex items-center gap-1.5 truncate" title={item.colegioDescripcion || item.codigoColegio}>
                          <Building2 className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          <span className="truncate">{item.colegioDescripcion || `Colegio ${item.codigoColegio}`}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Barra de Paginación */}
              {padron.totalPages > 1 && (
                <div className="flex items-center justify-between pt-4 pb-2 border-t border-slate-200">
                  <div className="text-xs text-slate-500 font-medium">
                    Página {padron.pageNumber} de {padron.totalPages}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={padron.pageNumber <= 1}
                      onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl border border-slate-200 bg-white text-slate-700 text-xs font-semibold hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      <span>Anterior</span>
                    </button>
                    <button
                      type="button"
                      disabled={padron.pageNumber >= padron.totalPages}
                      onClick={() => setPage((prev) => Math.min(prev + 1, padron.totalPages))}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl border border-slate-200 bg-white text-slate-700 text-xs font-semibold hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                    >
                      <span>Siguiente</span>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Bases y Condiciones Oficiales al pie */}
          <div className="mt-12 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 shadow-xs text-left">
            <div className="flex items-center gap-2 text-slate-900 font-bold text-sm sm:text-base mb-3">
              <Info className="w-4 h-4 text-brand-cyan" />
              <span>Condiciones y Bases del Sorteo</span>
            </div>
            <div className="space-y-2 text-xs sm:text-sm text-slate-600 leading-relaxed">
              <p>
                Participan todos los Martilleros y Corredores Públicos matriculados de la Provincia de Buenos Aires que reúnen las siguientes condiciones generales de forma automática, sin necesidad de inscribirse ni tener que realizar ningún trámite.
              </p>
              <p>
                <strong className="text-slate-800">Condiciones para participar:</strong> estar activo en la matrícula y tener una antigüedad de 1 año o más en el ejercicio profesional; estar al día con sus aportes previsionales y no tener convenios vigentes.
              </p>
            </div>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PadronSorteoView;
